import { Component, computed, inject } from '@angular/core';
import { MenuController } from '@ionic/angular';
import { map, startWith } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import {
    BaseAppComponent,
    CustomLoader,
    PurchasingService,
    PushRegistrationService,
    SelectTranslations,
    ThemeService
} from 'army-builder-shared';

import { environment } from '../environments/environment';
import pkg from '../../package.json';

import { MenuItem } from 'army-builder-shared';
import { WarlordUserService } from './settings/user/user.service';
import { SubscriptionService } from './subscription/subscription.service';
import { GAME_IDS, GAME_ROLE_RESTRICTIONS } from './game-id.token';
import { Capacitor } from '@capacitor/core';

// Per-child menu gate for the list-builder/library/settings entries under each game. MENU_GAMES is
// only shown to subscribers at all (see appPages$ below), so these child gates are defence in depth
// rather than the thing doing the hiding.
const SUBSCRIBER_ROLES = ['app_subscriber', 'app_subscriber_annual'];

@Component({
    standalone: false,
    selector: 'app-root',
    template: `
        <ion-app
            *ngLet="{
                otherClientActive: otherClientActive$ | async,
                hasAccess: subscriptionService.userHasAccess$ | async,
                appPages: appPages$ | async,
                translationState: translationState$ | async,
                isLoggedIn: isLoggedIn$ | async,
                userRoles: userService.user()?.roles || []
            } as props"
            [class]="[platformDescription, config.environment].join(' ')"
        >
            <div class="app-layout">
                <div class="warning-bars">
                    <div class="envWarning" *ngIf="config.environment === 'staging'">Staging environment</div>
                    <abs-impersonation-bar></abs-impersonation-bar>
                    <abs-billing-simulation-bar></abs-billing-simulation-bar>
                </div>
                <div class="content-area">
                    <ion-split-pane contentId="mainRouterOutlet" [disabled]="themeService.isDesktop()">
                        <ion-menu
                            type="overlay"
                            side="end"
                            contentId="mainRouterOutlet"
                            *ngIf="props.appPages && !themeService.isDesktop()"
                        >
                            <ion-header>
                                <ion-toolbar>
                                    <ion-title>{{ 'GLOBAL.MENU' | translate }}</ion-title>
                                </ion-toolbar>
                            </ion-header>
                            <ion-content>
                                <abs-menu
                                    style="list"
                                    [menuItems]="props.appPages || []"
                                    [roles]="props.userRoles"
                                    [isLoggedIn]="props.isLoggedIn || false"
                                    [mode]="'root'"
                                ></abs-menu>
                            </ion-content>
                            <ion-footer>
                                <a href="#" (click)="reportProblem($event)" class="support">{{ 'GLOBAL.SUPPORT.LINK' | translate }}</a>
                                |
                                <a href="mailto:warlord@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                                |
                                <span class="version">{{ appVersion }}</span>
                            </ion-footer>
                        </ion-menu>
                        <div class="main">
                            <div class="topBar">
                                <a [routerLink]="'/'"><img src="/assets/images/warlord.png" alt="Warlord" class="warlordLogo" /></a>
                                <abs-menu
                                    [style]="'menu'"
                                    [menuItems]="props.appPages || []"
                                    [isLoggedIn]="props.isLoggedIn || false"
                                    [roles]="props.userRoles"
                                    [mode]="'root'"
                                    [layout]="'horizontal'"
                                ></abs-menu>
                            </div>

                            <ion-router-outlet id="mainRouterOutlet" [animation]="null"></ion-router-outlet>

                            <div class="footer">
                                <a href="#" (click)="reportProblem($event)" class="support">{{ 'GLOBAL.SUPPORT.LINK' | translate }}</a>
                                |
                                <a href="mailto:warlord@maloric.com" class="contact">{{ 'GLOBAL.MENU_ITEMS.CONTACT' | translate }}</a>
                                |
                                <a class="version" [routerLink]="'/pages/patch-notes'">{{ appVersion }}</a>
                            </div>
                        </div>
                    </ion-split-pane>
                </div>
            </div>

            @if (urlEnvironment === 'staging' && config.environment && config.environment !== 'staging') {
                <div class="fullPageOverlay">
                    <ion-card>
                        <h1>Wrong Environment</h1>
                        <p>
                            It appears you are trying to access the staging environment while the app is configured for a different
                            environment.
                        </p>

                        <p>Admins: Please ensure that staging and production are in different folders.</p>
                    </ion-card>
                </div>
            }

            <div class="fullPageOverlay" *ngIf="supported === false">
                <ion-card>
                    <h1>{{ 'GLOBAL.UNSUPPORTED_CLIENT.TITLE' | translate }}</h1>
                    <p>
                        {{
                            'GLOBAL.UNSUPPORTED_CLIENT.MESSAGE'
                                | translate
                                    : {
                                          minClientVersion: this.minClientVersion,
                                          currentClientVersion: this.currentClientVersion
                                      }
                        }}
                    </p>
                    @if (platformDescription === 'android') {
                        <p>
                            <a href="https://play.google.com/store/apps/details?id=com.warlordgames.app&gl=GB"
                                ><img class="storeLogo" src="/assets/images/google-play.png" [alt]="'GLOBAL.HOME.GOOGLE' | translate"
                            /></a>
                        </p>
                    } @else if (platformDescription === 'ios') {
                        <p>
                            <a href="https://apps.apple.com/us/app/warlord-games-list-builder/id1637349658"
                                ><img class="storeLogo" src="/assets/images/app-store.png" [alt]="'GLOBAL.HOME.APPLE' | translate"
                            /></a>
                        </p>
                    }
                </ion-card>
            </div>

            <div class="useHere" *ngIf="props.otherClientActive">
                <ion-card>
                    <h1>{{ 'GLOBAL.UNSUPPORTED_CLIENT.TITLE' | translate }}</h1>
                    <p>
                        {{
                            'GLOBAL.UNSUPPORTED_CLIENT.MESSAGE'
                                | translate
                                    : {
                                          minClientVersion: this.minClientVersion,
                                          currentClientVersion: this.currentClientVersion
                                      }
                        }}
                    </p>
                </ion-card>
            </div>

            <abs-entity-preload-modal [gameIds]="gameIds()"></abs-entity-preload-modal>
            <abs-translation-preload-modal [gameIds]="gameIds()" [language]="language"></abs-translation-preload-modal>
            <abs-co-organiser-notification-handler></abs-co-organiser-notification-handler>
        </ion-app>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            .app-layout {
                display: flex;
                flex-direction: column;
                position: absolute;
                inset: 0;
            }

            .content-area {
                flex: 1;
                position: relative;
            }

            .envWarning {
                background: #ffcc00;
                color: black;
                font-weight: bold;
                width: 100%;
                height: 40px;
                line-height: 40px;
                text-align: center;
                font-family: 'Courier New', Courier, monospace;
            }

            .logo {
                max-width: 200px;
            }

            ion-footer {
                padding: 5px;
                background: #5b5b5b;
                color: white;
            }

            ion-footer a {
                color: white;
            }

            .main {
                width: 100%;
            }

            .version,
            .contact,
            .support {
                padding: 0 10px;
            }

            abs-entity-preload-modal,
            abs-translation-preload-modal {
                position: relative;
                z-index: 1;
            }

            .storeLogo {
                height: 85px;
            }

            @media screen and (max-width: 991px) {
                .topBar,
                .footer {
                    display: none;
                }

                ion-menu abs-menu {
                    display: block;
                }
            }

            @media screen and (min-width: 992px) {
                .content-area {
                    display: flex;
                    flex-direction: column;
                }

                ion-split-pane {
                    position: relative;
                    flex: 1;
                }

                .topBar {
                    background: black;
                    display: flex;
                    width: 100%;
                    height: 50px;
                    position: relative;
                    z-index: 2;
                }

                .topBar .warlordLogo {
                    height: 32px;
                    margin: 9px 20px 9px 9px;
                }

                .topBar abs-menu {
                    flex: 1 1 auto;
                }

                ion-menu {
                    display: none;
                }

                .footer {
                    background: #232323;
                    position: relative;
                    color: white;
                    text-align: center;
                    font-size: 12px;
                    z-index: 10;
                    height: var(--footer-height);
                    line-height: var(--footer-height);
                }
                .footer a {
                    color: #cfcfcf;
                    text-decoration: none;
                }
                .footer a:hover {
                    color: white;
                    transition:
                        all ease 0.3s,
                        text-decoration 0.1s ease;
                    text-decoration: underline;
                }

                .main {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }

                ion-router-outlet {
                    flex: 1 1 auto;
                    position: relative;
                    z-index: 1;
                }
            }
        `
    ]
})
export class AppComponent extends BaseAppComponent {
    affId = environment.affiliateId;
    appVersion: string = pkg.version;
    clearDataBeforeVersion: string = pkg.clearDataBeforeVersion;
    supported = null;
    minClientVersion = null;
    currentClientVersion = null;
    otherClientActive$ = new BehaviorSubject<boolean>(false);
    translationState$ = this.store.select(SelectTranslations);
    isLoggedIn$ = this.userService.loggedIn$;
    themeService = inject(ThemeService);

    gameIds = computed(() => {
        const roles = this.userService.user()?.roles || [];
        return ['global', ...Object.values(GAME_IDS)].filter(
            (id) => !GAME_ROLE_RESTRICTIONS[id] || roles.includes(GAME_ROLE_RESTRICTIONS[id])
        );
    });
    platformDescription = Capacitor.getPlatform();

    urlEnvironment: string;

    MENU_GAMES: MenuItem[] = [
        // {
        //     title: 'GLOBAL.MENU_ITEMS.BOLT_ACTION',
        //     children: [
        //         { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/bolt-action/forces' },
        //         { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/bolt-action/library' },
        //         { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/bolt-action/settings' }
        //     ],
        //     requiresLogin: true
        // },
        {
            title: 'GLOBAL.MENU_ITEMS.BOLT_ACTION_3E',
            children: [
                {
                    title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER',
                    icon: 'list',
                    url: '/bolt-action-3e/forces',
                    requiredRoles: SUBSCRIBER_ROLES
                },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/bolt-action-3e/library', requiredRoles: SUBSCRIBER_ROLES },
                // Not subscription-gated on purpose — any subscriber seeing this menu can browse/
                // register/submit a text list regardless of role; requiredFeature is the only gate.
                // Non-subscribers don't see this menu at all and use the global /events entry.
                { title: 'GLOBAL.MENU_ITEMS.EVENTS', icon: 'trophy', url: '/bolt-action-3e/events', requiredFeature: 'events' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/bolt-action-3e/settings', requiredRoles: SUBSCRIBER_ROLES }
            ],
            requiresLogin: true,
            requiredRoles: []
        },
        {
            title: 'GLOBAL.MENU_ITEMS.K47',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/k47/forces', requiredRoles: SUBSCRIBER_ROLES },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/k47/library', requiredRoles: SUBSCRIBER_ROLES },
                // See the identical comment on the bolt-action-3e Events entry above.
                { title: 'GLOBAL.MENU_ITEMS.EVENTS', icon: 'trophy', url: '/k47/events', requiredFeature: 'events' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/k47/settings', requiredRoles: SUBSCRIBER_ROLES }
            ],
            requiresLogin: true
            // requiredRoles: ['k47-early-access']
        },
        {
            title: 'GLOBAL.MENU_ITEMS.BLACK_SEAS',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/black-seas/forces' },
                { title: 'GLOBAL.MENU_ITEMS.BATTLE_TRACKER', icon: 'list', url: '/black-seas/battle' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/black-seas/settings' }
            ],
            // No Events entry in this game yet, so there is nothing a non-subscriber would want
            // here; the parent-level gate is kept as-is.
            requiresLogin: true,
            requiredRoles: SUBSCRIBER_ROLES
        },
        {
            title: 'GLOBAL.MENU_ITEMS.BLOOD_RED_SKIES',
            children: [
                { title: 'GLOBAL.MENU_ITEMS.LIST_BUILDER', icon: 'list', url: '/blood-red-skies/forces' },
                { title: 'GLOBAL.MENU_ITEMS.LIBRARY', icon: 'list', url: '/blood-red-skies/library' },
                { title: 'GLOBAL.MENU_ITEMS.SETTINGS', icon: 'cog', url: '/blood-red-skies/settings' }
            ],
            // Same as Black Seas above: no Events entry, so keep the parent-level gate.
            requiresLogin: true,
            requiredRoles: SUBSCRIBER_ROLES
        }
    ];

    // Top level for subscribers and non-subscribers alike — game-agnostic, so deliberately not
    // nested under any single game's menu (see MENU_GAMES above). No requiresLogin: whether an
    // anonymous visitor sees this is the flag's business, not login's. A flag restricted by role
    // or email reads false for them; an unrestricted enabled flag reads true, which is what
    // brings this back for everyone at general release.
    MENU_EVENTS: MenuItem = {
        title: 'GLOBAL.EVENTS.BROWSE_TITLE',
        icon: 'trophy',
        url: '/events',
        // Same gate as the per-game Events entries in MENU_GAMES. Without it this top-level
        // item showed to everyone, anonymous visitors included, which bypassed the flag that
        // is the whole mechanism for releasing events to a subset of users first.
        requiredFeature: 'events'
    };

    MENU_OTHER: MenuItem[] = [
        {
            // Kept first on purpose: abs-menu points a parent item's own link at children[0].url
            // without applying that child's requiresLogin, so the first entry here must be one an
            // anonymous visitor can actually reach — the top bar renders when logged out too.
            title: 'GLOBAL.MENU_ITEMS.PATCH_NOTES',
            icon: 'list',
            url: '/pages/patch-notes'
        },
        {
            title: 'GLOBAL.SETTINGS.ACCOUNT.MANAGE_SUBSCRIPTION',
            icon: 'card-outline',
            url: '/settings/subscription',
            requiresLogin: true
        },
        {
            title: 'GLOBAL.MENU_ITEMS.SETTINGS',
            icon: 'cog',
            url: '/settings',
            requiresLogin: true
        },
        {
            title: 'GLOBAL.MENU_ITEMS.SUPPORT_TICKETS',
            icon: 'flag-outline',
            url: '/support',
            requiresLogin: true
        },
        {
            title: 'GLOBAL.MENU_ITEMS.KNOWN_ISSUES',
            icon: 'bug-outline',
            url: '/pages/known-issues'
        },
        {
            title: 'GLOBAL.MENU_ITEMS.FAQ',
            icon: 'help-circle-outline',
            url: '/pages/faq'
        },
        {
            title: 'GLOBAL.MENU_ITEMS.EULA',
            icon: 'reader-outline',
            url: '/eula'
        }
    ];

    public appPages$: Observable<MenuItem[]> = combineLatest([
        this.userService.isStaff$,
        this.subscriptionService.userHasAccess$,
        this.userService.loggedIn$
    ]).pipe(
        // select(LoginSelector),
        map(([isAdmin, isSubscriber, isLoggedIn]) => {
            // Events and Other are always top level; only the games list is subscription-gated.
            // MENU_GAMES is subscriber-only because Bolt Action 3E / K47 have parents that are only
            // requiresLogin, so their per-child role gates would not hide the parent group itself —
            // a non-subscriber would see those two games with nothing usable inside. They reach
            // events via MENU_EVENTS instead; the per-game events routes stay unguarded, so shared
            // links keep working. Keeping MENU_OTHER nested (rather than spread) also holds the
            // non-subscriber top bar to a couple of items instead of nine.
            let menuItems: MenuItem[] = [
                ...(isSubscriber ? this.MENU_GAMES : []),
                this.MENU_EVENTS,
                {
                    title: 'GLOBAL.MENU_ITEMS.OTHER',
                    children: this.MENU_OTHER
                }
            ];

            menuItems.push({
                title: '',
                placeholder: true
            });

            if (isAdmin) {
                menuItems.push({
                    title: 'GLOBAL.MENU_ITEMS.ADMIN',
                    icon: 'create',
                    url: '/admin'
                });

                // menuItems[0].children?.push({
                //     title: 'GLOBAL.MENU_ITEMS.COLLECTION_MANAGER',
                //     icon: 'list',
                //     url: '/bolt-action-3e/collection-manager'
                // });
            }

            if (isLoggedIn) {
                menuItems.push({
                    title: 'GLOBAL.LOGIN.BUTTONS.LOGOUT',
                    icon: 'log-out-outline',
                    onClick: () => this.userService.logout()
                });
            }
            return menuItems;
        })
    );

    constructor(
        protected userService: WarlordUserService,
        protected menu: MenuController,
        protected subscriptionService: SubscriptionService,
        protected purchaseService: PurchasingService,
        protected customLoader: CustomLoader,
        protected pushRegistrationService: PushRegistrationService
    ) {
        super();
        this.userService.loggedIn$.subscribe((loggedIn) => {
            this.menu.enable(loggedIn);
            if (loggedIn) {
                this.pushRegistrationService.init().catch((e) => console.error('Push registration failed', e));
            }
        });

        if (window.location.href.includes('staging')) {
            this.urlEnvironment = 'staging';
        } else if (window.location.href.includes('localhost')) {
            this.urlEnvironment = 'localhost';
        } else {
            this.urlEnvironment = 'production';
        }
    }

    reportProblem(e?) {
        if (e) {
            e.preventDefault();
        }
        this.supportService.showModal();
    }

    init() {}
}
